import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Copy, Check, MapPin, Truck, CreditCard, Smartphone, ChevronLeft, ChevronRight } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import useEmblaCarousel from 'embla-carousel-react';

const WHATSAPP_NUMBER = "919381690689"; // ✅ FIXED (removed +)
const UPI_ID = "8977093651-2@ybl";

// Modal Image Carousel
const ModalImageCarousel = ({ images, title }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative h-full">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {images.map((image, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0 h-full">
              <img
                src={image}
                alt={`${title} - ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <>
          <button onClick={scrollPrev} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <button onClick={scrollNext} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center">
            <ChevronRight size={22} />
          </button>
        </>
      )}
    </div>
  );
};

const ArtworkDetailModal = ({ artwork, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  if (!artwork) return null;

  const images = artwork.images || [artwork.image];

  const handleCopyUPI = async () => {
    await navigator.clipboard.writeText(UPI_ID);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ✅ FIXED WHATSAPP (uses wa.me + correct number)
  const handleWhatsAppOrder = () => {
    const message = encodeURIComponent(
      `Hi! I'm interested in purchasing "${artwork.title}" (${artwork.size}) priced at ${artwork.price}.`
    );

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const handleWhatsAppPaymentConfirm = () => {
    const message = encodeURIComponent(
      `Hi! I have completed the payment for "${artwork.title}" (${artwork.price}). Sharing screenshot.`
    );

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const upiPaymentString = `upi://pay?pa=${UPI_ID}&pn=Ethereal%20Calligraphy&am=${artwork.price.replace('₹', '').replace(',', '')}&cu=INR`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
          <div className="absolute inset-0 bg-black/95" />

          <motion.div className="relative bg-[#111] rounded-2xl max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            
            <button className="absolute top-4 right-4" onClick={onClose}>
              <X size={20} />
            </button>

            <div className="grid lg:grid-cols-2">

              <div className="h-[400px]">
                <ModalImageCarousel images={images} title={artwork.title} />
              </div>

              <div className="p-6">

                <h2 className="text-2xl text-yellow-400">{artwork.title}</h2>

                <p className="text-white text-xl">{artwork.price}</p>

                <p className="text-gray-300">{artwork.meaning}</p>

                {/* BUTTONS */}
                <button
                  className="w-full mt-4 bg-green-500 p-3 text-white rounded"
                  onClick={handleWhatsAppOrder}
                >
                  Buy on WhatsApp
                </button>

                <button
                  className="w-full mt-2 bg-yellow-400 p-3 text-black rounded"
                  onClick={() => setShowPayment(true)}
                >
                  Scan & Pay
                </button>

                {showPayment && (
                  <div className="mt-4">
                    <QRCodeSVG value={upiPaymentString} size={150} />

                    <p>{UPI_ID}</p>

                    <button onClick={handleCopyUPI}>
                      {copied ? "Copied!" : "Copy"}
                    </button>

                    <button
                      className="w-full mt-2 bg-green-500 p-3 text-white rounded"
                      onClick={handleWhatsAppPaymentConfirm}
                    >
                      Send Screenshot
                    </button>
                  </div>
                )}

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ArtworkDetailModal;